import { BaseClient } from '../BaseClient';
import { Bool, Empty } from '../compiled-protos/common';
import {
  SCBadgeRequest,
  SCBadgeResponse,
  SCCertificationCardFields,
  SCCertificationCardImage,
  SCCertificationStatus,
  SCChecklist,
  SCChecklistAnswer,
  SCChecklistQuestion,
  SCChecklistQuestionOption,
  SCChecklistRequest,
  SCChecklistResponse,
  SCCreateCertificationRequest,
  SCCustomerRef,
  SCCustomerSettings,
  SCDeclineInboundRequest,
  SCExchangeCodeRequest,
  SCGetAuthURLRequest,
  SCGetAuthURLResponse,
  SCInboundRef,
  SCInboundWorkOrder,
  SCInboundWorkOrderList,
  SCLinkRequest,
  SCLinkResponse,
  SCMappingRef,
  SCPhoneCheckIn,
  SCPriorityMapping,
  SCPriorityMappingList,
  SCRefreshTokenRequest,
  SCRetrySyncEventRequest,
  SCSaveChecklistAnswersRequest,
  SCSetCustomerEnabledRequest,
  SCSetStoreAutoAcceptRequest,
  SCStatusMapping,
  SCStatusMappingList,
  SCStoreAutoAccept,
  SCStoreAutoAcceptChange,
  SCSubmitProposalRequest,
  SCSubscriberSummary,
  SCSubscriberSummaryList,
  SCSyncLogEntry,
  SCTechnicianCertification,
  SCTechnicianCertificationList,
  SCTechnicianMapping,
  SCTechnicianMappingList,
  SCTechnicianRef,
  SCTradeMapping,
  SCTradeMappingList,
  SCUnmappedTrade,
  SCUnmappedTradeList,
  SCVisitOutcome,
  SCVisitRequest,
} from '../compiled-protos/servicechannel';
import { ServiceChannelServiceClient } from '../compiled-protos/servicechannel.client';

class ServiceChannelClient extends BaseClient {
  self: ServiceChannelServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new ServiceChannelServiceClient(this.transport);
  }

  public async GetAuthURL(req: SCGetAuthURLRequest) {
    let res = SCGetAuthURLResponse.create();
    try {
      res = await this.self.getAuthURL(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async ExchangeCode(req: SCExchangeCodeRequest) {
    let res = Bool.create();
    try {
      res = await this.self.exchangeCode(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async RefreshToken(req: SCRefreshTokenRequest) {
    let res = Bool.create();
    try {
      res = await this.self.refreshToken(req, this.getMetaData()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  /**
   * Returns the checklists a job requires, empty for jobs that require none.
   *
   * Note this deliberately does NOT swallow errors the way the OAuth calls
   * above do. Those are best-effort; this one gates job completion, and a
   * caller that cannot tell "no checklist" from "the request failed" would let
   * a tech complete a job whose checklist was never shown to them. Callers
   * must decide what a failure means rather than reading it as an empty list.
   */
  public async GetChecklist(req: SCChecklistRequest) {
    return await this.self.getChecklist(req, this.getMetaData()).response;
  }

  /**
   * Records checklist answers and queues them for ServiceChannel.
   *
   * Also propagates errors on purpose: these answers are a tech's only record
   * of work they may have already left the site for, so a failed save has to
   * surface rather than resolve to undefined and look like success.
   */
  public async SaveChecklistAnswers(req: SCSaveChecklistAnswersRequest) {
    return await this.self.saveChecklistAnswers(req, this.getMetaData())
      .response;
  }

  /**
   * Returns a job's ServiceChannel identity and the connector's recent traffic
   * for it, or `linked: false` for the overwhelming majority of Kalos jobs.
   *
   * Errors propagate, for the same reason GetChecklist's do. A swallowed
   * failure here resolves to undefined, which a caller reads as "not a
   * ServiceChannel job" — so an outage would silently hide the panel, the
   * badge and the send routing on jobs that genuinely are ServiceChannel work,
   * and staff would email a proposal to a subscriber who cannot receive one.
   * "We could not tell" and "it is not linked" have to stay distinguishable.
   */
  public async GetJobLink(req: SCLinkRequest) {
    return await this.self.getJobLink(req, this.getMetaData()).response;
  }

  /** Whether one job, property or customer is a ServiceChannel record. */
  public async GetBadge(req: SCBadgeRequest) {
    return await this.self.getBadge(req, this.getMetaData()).response;
  }

  /**
   * Queues a Kalos quote as a ServiceChannel proposal.
   *
   * Resolving means QUEUED, not delivered — the send happens on the backend's
   * outbound drain so ServiceChannel being slow never blocks the click. UI must
   * not claim "Submitted" off the back of this; the sync log entry's
   * processedAt is what says it actually reached them.
   */
  public async SubmitProposal(req: SCSubmitProposalRequest) {
    return await this.self.submitProposal(req, this.getMetaData()).response;
  }

  /** Puts a failed ledger row back on its drain. */
  public async RetrySyncEvent(req: SCRetrySyncEventRequest) {
    return await this.self.retrySyncEvent(req, this.getMetaData()).response;
  }

  /**
   * Sends one On Call row's check-in to ServiceChannel now and answers where
   * it stands (86bc18e7q): recorded, not_recorded (with the refusal and the
   * distance), pending, failed, skipped or not_linked. Idempotent for a visit
   * already recorded; a refused attempt is sent again with the row's current
   * coordinate, so update latitude/longitude first to retry from a new spot.
   *
   * Errors are not swallowed: a transport failure here means the connector
   * still has the row queued, and the caller shows "not gone through yet".
   */
  public async SendVisitCheckIn(req: SCVisitRequest) {
    return await this.self.sendVisitCheckIn(req, this.getMetaData()).response;
  }

  /**
   * The check-out twin of SendVisitCheckIn (86bc12cgh): sends the Completed or
   * Incomplete row's check-out now and answers where it stands. Idempotent for
   * a check-out already delivered; the resend for a refused one. Never moves
   * the job's Kalos status: a held Completed moves to Admin Review only when
   * ServiceChannel accepts the push. FailedPrecondition when ServiceChannel
   * already shows the work order completed or invoiced.
   */
  public async SendVisitCheckOut(req: SCVisitRequest) {
    return await this.self.sendVisitCheckOut(req, this.getMetaData()).response;
  }

  /*
   * EPA 608 cards (86bc12cgh). A refrigerant Yes at close-out has to be signed
   * by a technician with a card on file. "On file" is ServiceChannel's provider
   * record when the Contractor Tools user is configured and the technician is
   * mapped, and Kalos's own mirror otherwise.
   */

  /** Whether one technician (userId 0 = the caller) can sign a refrigerant Yes. */
  public async GetSCTechnicianCertification(req: SCTechnicianRef) {
    return await this.self.getSCTechnicianCertification(req, this.getMetaData()).response;
  }

  /** Active employees with an unexpired card, one per technician. */
  public async ListSCCertifiedTechnicians() {
    return await this.self.listSCCertifiedTechnicians(Empty.create(), this.getMetaData())
      .response;
  }

  /**
   * Records a card (photo + the fields) for the caller, or for anyone when the
   * caller is a ServiceChannel admin, and pushes it to ServiceChannel when it
   * can. InvalidArgument messages are user-readable (missing number, date or
   * photo; an expired card).
   */
  public async CreateSCTechnicianCertification(req: SCCreateCertificationRequest) {
    return await this.self.createSCTechnicianCertification(req, this.getMetaData()).response;
  }

  /**
   * Reads the card's number, level and dates off a photo to prefill the
   * add-card form. Every field stays editable; `readable` false means nothing
   * could be read and the form starts empty.
   */
  public async ParseSCCertificationCard(req: SCCertificationCardImage) {
    return await this.self.parseSCCertificationCard(req, this.getMetaData()).response;
  }

  /**
   * What the technician needs to check in by phone when ServiceChannel refused
   * the GPS check-in (86bc18e7q): the line to call, their PIN (their
   * ServiceChannel user id, entered by dispatch on the mappings screen) and the
   * work order number. `available` false carries a plain-English reason.
   *
   * Errors propagate: "we could not tell" must not read as "not available".
   */
  public async GetPhoneCheckIn(req: SCLinkRequest) {
    return await this.self.getPhoneCheckIn(req, this.getMetaData()).response;
  }

  /**
   * Tells dispatch the technician is checking in by phone (activity line on
   * the job + Chat card). Nothing is sent to ServiceChannel; the call is the
   * check-in there.
   */
  public async ReportPhoneCheckIn(req: SCLinkRequest) {
    return await this.self.reportPhoneCheckIn(req, this.getMetaData()).response;
  }

  /*
   * Mapping administration.
   *
   * These deliberately do NOT swallow errors the way the older OAuth methods
   * above do. The backend answers a duplicate with an INVALID_ARGUMENT naming
   * the conflict ("that trade is already mapped…"), and a caller without the
   * ServiceChannelAdmin permission with PERMISSION_DENIED. Both are the only
   * useful thing the form can show, so both have to reach it.
   */

  /** Subscribers for the mapping screen's selector; the default is first. */
  public async ListSCSubscribers() {
    return await this.self.listSCSubscribers(Empty.create(), this.getMetaData()).response;
  }

  /**
   * Trades that have actually refused a work order, with how many are waiting.
   *
   * Read off the ledger rather than typed. Trade matching is an exact string
   * compare against a vocabulary ServiceChannel exposes no endpoint for, so a
   * hand-typed name that differs by a character never matches and fails
   * silently — offering the real strings removes the opportunity.
   */
  public async ListSCUnmappedTrades() {
    return await this.self.listSCUnmappedTrades(Empty.create(), this.getMetaData()).response;
  }

  /** subscriberConfigId 0 means every subscriber, not the default rows only. */
  public async BatchGetSCTradeMapping(req: SCTradeMapping) {
    return await this.self.batchGetSCTradeMapping(req, this.getMetaData()).response;
  }

  public async CreateSCTradeMapping(req: SCTradeMapping) {
    return await this.self.createSCTradeMapping(req, this.getMetaData()).response;
  }

  public async UpdateSCTradeMapping(req: SCTradeMapping) {
    return await this.self.updateSCTradeMapping(req, this.getMetaData()).response;
  }

  public async DeleteSCTradeMapping(req: SCMappingRef) {
    return await this.self.deleteSCTradeMapping(req, this.getMetaData()).response;
  }

  public async BatchGetSCPriorityMapping(req: SCPriorityMapping) {
    return await this.self.batchGetSCPriorityMapping(req, this.getMetaData()).response;
  }

  public async CreateSCPriorityMapping(req: SCPriorityMapping) {
    return await this.self.createSCPriorityMapping(req, this.getMetaData()).response;
  }

  public async UpdateSCPriorityMapping(req: SCPriorityMapping) {
    return await this.self.updateSCPriorityMapping(req, this.getMetaData()).response;
  }

  public async DeleteSCPriorityMapping(req: SCMappingRef) {
    return await this.self.deleteSCPriorityMapping(req, this.getMetaData()).response;
  }

  public async BatchGetSCStatusMapping(req: SCStatusMapping) {
    return await this.self.batchGetSCStatusMapping(req, this.getMetaData()).response;
  }

  public async CreateSCStatusMapping(req: SCStatusMapping) {
    return await this.self.createSCStatusMapping(req, this.getMetaData()).response;
  }

  public async UpdateSCStatusMapping(req: SCStatusMapping) {
    return await this.self.updateSCStatusMapping(req, this.getMetaData()).response;
  }

  public async DeleteSCStatusMapping(req: SCMappingRef) {
    return await this.self.deleteSCStatusMapping(req, this.getMetaData()).response;
  }

  /** Technician -> ServiceChannel user id (the phone check-in PIN). */
  public async BatchGetSCTechnicianMapping() {
    return await this.self.batchGetSCTechnicianMapping(Empty.create(), this.getMetaData())
      .response;
  }

  public async UpsertSCTechnicianMapping(req: SCTechnicianMapping) {
    return await this.self.upsertSCTechnicianMapping(req, this.getMetaData()).response;
  }

  public async DeleteSCTechnicianMapping(req: SCTechnicianRef) {
    return await this.self.deleteSCTechnicianMapping(req, this.getMetaData()).response;
  }

  /**
   * The ServiceChannel section on a customer record: whether the integration is
   * enabled for the customer, and which of their stores auto-accept work
   * orders. Open to any signed-in caller; canEdit says whether the caller is in
   * ServiceChannelAdmin, which both writes below require server-side.
   */
  public async GetSCCustomerSettings(customerUserId: number) {
    return await this.self.getSCCustomerSettings(SCCustomerRef.create({ customerUserId }), this.getMetaData())
      .response;
  }

  public async SetSCCustomerEnabled(req: SCSetCustomerEnabledRequest) {
    return await this.self.setSCCustomerEnabled(req, this.getMetaData()).response;
  }

  /** Applies every change in one transaction; unchanged stores are no-ops. */
  public async SetSCStoreAutoAccept(req: SCSetStoreAutoAcceptRequest) {
    return await this.self.setSCStoreAutoAccept(req, this.getMetaData()).response;
  }

  /**
   * Inbound work orders (86bc12jtg): work orders for stores with auto-accept
   * off, waiting for a CSR to accept or decline. List returns pending ones,
   * soonest SLA first; Get returns one in any state. Open to any signed-in
   * employee.
   */
  public async ListSCInboundWorkOrders() {
    return await this.self.listSCInboundWorkOrders(Empty.create(), this.getMetaData()).response;
  }

  public async GetSCInboundWorkOrder(scWoId: number) {
    return await this.self.getSCInboundWorkOrder(
      SCInboundRef.create({ scWoId: BigInt(scWoId) }),
      this.getMetaData(),
    ).response;
  }

  /** Creates the job and accepts the work order in ServiceChannel. Cannot be undone. */
  public async AcceptSCInboundWorkOrder(scWoId: number) {
    return await this.self.acceptSCInboundWorkOrder(
      SCInboundRef.create({ scWoId: BigInt(scWoId) }),
      this.getMetaData(),
    ).response;
  }

  /** Declines with a reason the customer reads on the work order. No job is created. */
  public async DeclineSCInboundWorkOrder(scWoId: number, reason: string) {
    return await this.self.declineSCInboundWorkOrder(
      SCDeclineInboundRequest.create({ scWoId: BigInt(scWoId), reason }),
      this.getMetaData(),
    ).response;
  }
}

export {
  SCCertificationCardFields,
  SCCertificationCardImage,
  SCCertificationStatus,
  SCCreateCertificationRequest,
  SCCustomerRef,
  SCCustomerSettings,
  SCDeclineInboundRequest,
  SCTechnicianCertification,
  SCTechnicianCertificationList,
  SCInboundRef,
  SCInboundWorkOrder,
  SCInboundWorkOrderList,
  SCMappingRef,
  SCPhoneCheckIn,
  SCPriorityMapping,
  SCPriorityMappingList,
  SCStatusMapping,
  SCStatusMappingList,
  SCSubscriberSummary,
  SCSubscriberSummaryList,
  SCTradeMapping,
  SCTradeMappingList,
  SCUnmappedTrade,
  SCUnmappedTradeList,
  SCBadgeRequest,
  SCBadgeResponse,
  SCChecklist,
  SCChecklistAnswer,
  SCChecklistQuestion,
  SCChecklistQuestionOption,
  SCChecklistRequest,
  SCChecklistResponse,
  SCExchangeCodeRequest,
  SCGetAuthURLRequest,
  SCGetAuthURLResponse,
  SCLinkRequest,
  SCLinkResponse,
  SCRefreshTokenRequest,
  SCRetrySyncEventRequest,
  SCSaveChecklistAnswersRequest,
  SCSetCustomerEnabledRequest,
  SCSetStoreAutoAcceptRequest,
  SCStoreAutoAccept,
  SCStoreAutoAcceptChange,
  SCSubmitProposalRequest,
  SCSyncLogEntry,
  SCTechnicianMapping,
  SCTechnicianMappingList,
  SCTechnicianRef,
  SCVisitOutcome,
  SCVisitRequest,
  ServiceChannelClient,
};
